'use client';

import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useBooking } from '@/contexts/BookingContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, X } from 'lucide-react';
import {
	BookingStep,
	GiftCardRecipient,
	GiftCardPurchaseInput,
} from '@/types/booking';
import SearchableCountrySelect from '@/components/ui/SearchableCountrySelect';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { useInitiateGiftCardPurchase } from '@/lib/apollo/hooks/booking';
import { useLazyValidatePhoneNumber } from '@/lib/apollo/hooks/booking-edge';
import { useLazySearchClient } from '@/lib/apollo/hooks/booking-edge';
import { formatPrice, getBusinessCurrency } from '@/lib/utils';
import {
	PaymentProviderModal,
} from '@/components/booking/PaymentProviderModal';
import { StripeCheckoutModal } from '@/components/booking/StripeCheckoutModal';
import {
	getPaymentOptionConfig,
	getPaymentOptionsForCountry,
	type PaymentOption,
} from '@/lib/payment-providers';

const cleanPhone = (value: string) => value.replace(/[^0-9]/g, '');

declare global {
	interface Window {
		PaystackPop: any;
	}
}

const parseFullName = (fullName: string) => {
	const parts = fullName.trim().split(/\s+/);
	const firstName = parts[0] || '';
	const lastName = parts.slice(1).join(' ') || firstName;
	return { firstName, lastName };
};

const normalizeSpaces = (value: string) => value.trim().replace(/\s+/g, ' ');
const normalizeEmail = (value: string) => value.trim().toLowerCase();

const formatPhoneDisplay = (value: string) => {
	const cleanValue = value.replace(/[^0-9]/g, '');

	if (cleanValue.length <= 3) return cleanValue;
	if (cleanValue.length <= 6)
		return `${cleanValue.slice(0, 3)} ${cleanValue.slice(3)}`;
	if (cleanValue.length <= 10)
		return `${cleanValue.slice(0, 3)} ${cleanValue.slice(3, 6)} ${cleanValue.slice(6)}`;
	return `${cleanValue.slice(0, 4)} ${cleanValue.slice(4, 7)} ${cleanValue.slice(7, 11)}`;
};

export default function GiftCardCheckout() {
	useScrollToTop();

	const {
		selectedSalon,
		businessInfo,
		serviceClient,
		giftCardCart,
		updateGiftCardCartQuantity,
		clearGiftCardCart,
		addToast,
		handleNextStep,
	} = useBooking();
	const { user } = useAuth();

	const giftCardSetting = businessInfo?.giftCardSettingForBooking;
	const currency = getBusinessCurrency(businessInfo);
	const currencySymbol = currency.symbol;

	const minAmount = giftCardSetting?.minAmount ?? 0;
	const maxAmount = giftCardSetting?.maxAmount ?? 0;
	const cartUnitAmount = giftCardCart?.amount ?? 0;
	const cartQuantity = giftCardCart?.quantity ?? 1;
	const cartTotalAmount = cartUnitAmount * cartQuantity;

	const defaultBuyer = useMemo<GiftCardRecipient>(() => {
		return {
			firstName: serviceClient?.firstName || user?.firstName || '',
			lastName: serviceClient?.lastName || user?.lastName || '',
			email: serviceClient?.email || user?.email || '',
			countryCode:
				serviceClient?.countryCode || selectedSalon?.countryCode || 'NG',
			callingCode:
				serviceClient?.callingCode || selectedSalon?.callingCode || '+234',
			phone: serviceClient?.phone || user?.phone || '',
		};
	}, [serviceClient, selectedSalon, user]);

	const defaultRecipient = useMemo<GiftCardRecipient>(() => {
		return {
			firstName: '',
			lastName: '',
			email: '',
			countryCode: selectedSalon?.countryCode || 'NG',
			callingCode: selectedSalon?.callingCode || '+234',
			phone: '',
		};
	}, [selectedSalon]);

	const [purchaseType, setPurchaseType] = useState<'self' | 'gift'>('self');
	const [buyerFullName, setBuyerFullName] = useState('');
	const [recipientFullName, setRecipientFullName] = useState('');
	const [recipient, setRecipient] =
		useState<GiftCardRecipient>(defaultRecipient);
	const [buyer, setBuyer] = useState<GiftCardRecipient>(defaultBuyer);
	const [loading, setLoading] = useState(false);
	const [showProviderModal, setShowProviderModal] = useState(false);
	const [showStripeCheckout, setShowStripeCheckout] = useState(false);
	const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(
		null,
	);
	const [paystackLoaded, setPaystackLoaded] = useState(false);
	const [pocketLoaded, setPocketLoaded] = useState(false);
	const [showSuccessDialog, setShowSuccessDialog] = useState(false);
	const paymentCountryCode = selectedSalon?.countryCode || businessInfo?.countryCode;
	const availablePaymentOptions = getPaymentOptionsForCountry(paymentCountryCode);
	const [processingFeeAmount, setProcessingFeeAmount] = useState<number | null>(
		null,
	);
	const [successSummary, setSuccessSummary] = useState<{
		amount: number;
		message: string;
		businessName: string;
		currencySymbol: string;
	} | null>(null);

	const [initiateGiftCardPurchase] = useInitiateGiftCardPurchase();
	const [validatePhoneNumberQuery] = useLazyValidatePhoneNumber();
	const [searchClientQuery] = useLazySearchClient();

	const [buyerPhoneValidation, setBuyerPhoneValidation] = useState<{
		isValid: boolean | null;
		loading: boolean;
	}>({ isValid: null, loading: false });

	const [recipientPhoneValidation, setRecipientPhoneValidation] = useState<{
		isValid: boolean | null;
		loading: boolean;
	}>({ isValid: null, loading: false });

	const [buyerSearchLoading, setBuyerSearchLoading] = useState(false);
	const [recipientSearchLoading, setRecipientSearchLoading] = useState(false);
	const buyerLastSearchRef = useRef<string>('');
	const recipientLastSearchRef = useRef<string>('');
	const [buyerMatchedClient, setBuyerMatchedClient] = useState<{
		phone: string;
		fullName: string;
		email: string;
	} | null>(null);
	const [recipientMatchedClient, setRecipientMatchedClient] = useState<{
		phone: string;
		fullName: string;
		email: string;
	} | null>(null);

	useEffect(() => {
		setBuyer(defaultBuyer);
		setBuyerFullName(
			[defaultBuyer.firstName, defaultBuyer.lastName].filter(Boolean).join(' '),
		);
	}, [defaultBuyer]);

	useEffect(() => {
		setRecipient(defaultRecipient);
		setRecipientFullName('');
	}, [defaultRecipient]);

	useEffect(() => {
		const cleaned = cleanPhone(buyer.phone || '');

		if (cleaned.length >= 4) {
			setBuyerPhoneValidation((prev) => ({ ...prev, loading: true }));

			const timeoutId = setTimeout(async () => {
				try {
					const { data } = await validatePhoneNumberQuery({
						countryCode: buyer.countryCode,
						phoneNumber: cleaned,
					});
					const valid = data?.validatePhoneNumber?.numberValid;
					setBuyerPhoneValidation({ isValid: valid ?? null, loading: false });
				} catch (error) {
					console.error('Buyer phone validation error:', error);
					setBuyerPhoneValidation((prev) => ({ ...prev, loading: false }));
				}
			}, 500);

			return () => clearTimeout(timeoutId);
		}

		setBuyerPhoneValidation({ isValid: null, loading: false });
	}, [buyer.phone, buyer.countryCode, validatePhoneNumberQuery]);

	useEffect(() => {
		if (!selectedSalon?.id) return;

			const cleaned = cleanPhone(buyer.phone || '');
			if (cleaned.length < 10) {
				buyerLastSearchRef.current = '';
				setBuyerSearchLoading(false);
				setBuyerMatchedClient(null);
				return;
			}

		const searchKey = `${buyer.countryCode}:${cleaned}`;
		if (buyerLastSearchRef.current === searchKey) return;

		const timeoutId = setTimeout(async () => {
			setBuyerSearchLoading(true);
			try {
				const { data } = await searchClientQuery({
					salonId: selectedSalon.id,
					phone: cleaned,
					countryCode: buyer.countryCode,
				});

					const existingClient = data?.clientSearch;
					if (existingClient) {
						const matchedFullName = normalizeSpaces(
							[existingClient.firstName, existingClient.lastName]
								.filter(Boolean)
								.join(' '),
						);
						const matchedEmail = normalizeEmail(existingClient.email || '');
						setBuyer((prev) => ({
							...prev,
							firstName: prev.firstName || existingClient.firstName || '',
							lastName: prev.lastName || existingClient.lastName || '',
							email: prev.email || existingClient.email || '',
							phone: cleaned,
						}));
					setBuyerFullName((prev) =>
						prev.trim()
							? prev
							: [existingClient.firstName, existingClient.lastName]
									.filter(Boolean)
									.join(' '),
						);
						setBuyerMatchedClient({
							phone: cleaned,
							fullName: matchedFullName,
							email: matchedEmail,
						});
						addToast({
							message: `Welcome back, ${existingClient.firstName || 'there'}!`,
							variant: 'success',
						});
					}
			} catch {
				// ignore search errors; user can still fill form manually
			} finally {
				buyerLastSearchRef.current = searchKey;
				setBuyerSearchLoading(false);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [buyer.phone, buyer.countryCode, selectedSalon?.id, searchClientQuery, addToast]);

	useEffect(() => {
		const cleaned = cleanPhone(recipient.phone || '');

		if (cleaned.length >= 4) {
			setRecipientPhoneValidation((prev) => ({ ...prev, loading: true }));

			const timeoutId = setTimeout(async () => {
				try {
					const { data } = await validatePhoneNumberQuery({
						countryCode: recipient.countryCode,
						phoneNumber: cleaned,
					});
					const valid = data?.validatePhoneNumber?.numberValid;
					setRecipientPhoneValidation({
						isValid: valid ?? null,
						loading: false,
					});
				} catch (error) {
					console.error('Recipient phone validation error:', error);
					setRecipientPhoneValidation((prev) => ({ ...prev, loading: false }));
				}
			}, 500);

			return () => clearTimeout(timeoutId);
		}

		setRecipientPhoneValidation({ isValid: null, loading: false });
	}, [recipient.phone, recipient.countryCode, validatePhoneNumberQuery]);

	useEffect(() => {
		if (purchaseType !== 'gift') return;
		if (!selectedSalon?.id) return;

			const cleaned = cleanPhone(recipient.phone || '');
			if (cleaned.length < 10) {
				recipientLastSearchRef.current = '';
				setRecipientSearchLoading(false);
				setRecipientMatchedClient(null);
				return;
			}

		const searchKey = `${recipient.countryCode}:${cleaned}`;
		if (recipientLastSearchRef.current === searchKey) return;

		const timeoutId = setTimeout(async () => {
			setRecipientSearchLoading(true);
			try {
				const { data } = await searchClientQuery({
					salonId: selectedSalon.id,
					phone: cleaned,
					countryCode: recipient.countryCode,
				});

					const existingClient = data?.clientSearch;
					if (existingClient) {
						const matchedFullName = normalizeSpaces(
							[existingClient.firstName, existingClient.lastName]
								.filter(Boolean)
								.join(' '),
						);
						const matchedEmail = normalizeEmail(existingClient.email || '');
						setRecipient((prev) => ({
							...prev,
							firstName: prev.firstName || existingClient.firstName || '',
							lastName: prev.lastName || existingClient.lastName || '',
							email: prev.email || existingClient.email || '',
							phone: cleaned,
						}));
					setRecipientFullName((prev) =>
						prev.trim()
							? prev
							: [existingClient.firstName, existingClient.lastName]
									.filter(Boolean)
									.join(' '),
						);
						setRecipientMatchedClient({
							phone: cleaned,
							fullName: matchedFullName,
							email: matchedEmail,
						});
					}
			} catch {
				// ignore search errors; user can still fill form manually
			} finally {
				recipientLastSearchRef.current = searchKey;
				setRecipientSearchLoading(false);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [
		purchaseType,
		recipient.phone,
		recipient.countryCode,
		selectedSalon?.id,
		searchClientQuery,
	]);

	// Load both payment provider scripts on mount
	useEffect(() => {
		// Load Paystack (only if not already loaded)
		if (!document.querySelector('script[src*="js.paystack.co"]')) {
			const paystackScript = document.createElement('script');
			paystackScript.src = 'https://js.paystack.co/v2/inline.js';
			paystackScript.async = true;
			paystackScript.onload = () => setPaystackLoaded(true);
			document.body.appendChild(paystackScript);
		} else if (window.PaystackPop) {
			setPaystackLoaded(true);
		}

		// Load Pocket (only if not already loaded)
		if (!document.querySelector('script[src*="pocket-checkout-sdk"]')) {
			const pocketScript = document.createElement('script');
			pocketScript.src =
				'https://pocket-checkout-sdk.netlify.app/checkout-sdk.js';
			pocketScript.async = true;
			pocketScript.onload = () => setPocketLoaded(true);
			document.body.appendChild(pocketScript);
		} else if ((window as any).Pocket) {
			setPocketLoaded(true);
		}
	}, []);

	const validate = () => {
		if (!selectedSalon?.id) {
			addToast({
				message: 'Please select a location to continue.',
				variant: 'error',
			});
			return false;
		}

		if (!giftCardSetting?.enabled) {
			addToast({
				message: 'Gift cards are not available right now.',
				variant: 'error',
			});
			return false;
		}

		if (!giftCardCart) {
			addToast({
				message: 'Please add a gift card to cart to continue.',
				variant: 'error',
			});
			return false;
		}

		const amountNumber = cartTotalAmount;
		if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
			addToast({ message: 'Please enter a valid amount.', variant: 'error' });
			return false;
		}

		if (minAmount && amountNumber < minAmount) {
			addToast({
				message: `Amount must be at least ${formatPrice(minAmount, currency)}`,
				variant: 'error',
			});
			return false;
		}

		if (maxAmount && amountNumber > maxAmount) {
			addToast({
				message: `Amount must not exceed ${formatPrice(maxAmount, currency)}`,
				variant: 'error',
			});
			return false;
		}

		const validatePerson = ({
			person,
			fullName,
			label,
			phoneValidation,
		}: {
			person: GiftCardRecipient;
			fullName: string;
			label: string;
			phoneValidation?: { isValid: boolean | null };
		}) => {
			const { firstName, lastName } = parseFullName(fullName);
			if (!firstName.trim()) {
				addToast({
					message: `${label} first name is required.`,
					variant: 'error',
				});
				return false;
			}
			if (!lastName.trim()) {
				addToast({
					message: `${label} last name is required.`,
					variant: 'error',
				});
				return false;
			}
			if (!person.email?.trim()) {
				addToast({ message: `${label} email is required.`, variant: 'error' });
				return false;
			}
			if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email.trim())) {
				addToast({ message: `${label} email is invalid.`, variant: 'error' });
				return false;
			}
			if (!cleanPhone(person.phone)) {
				addToast({
					message: `${label} phone number is required.`,
					variant: 'error',
				});
				return false;
			}
			if (
				phoneValidation?.isValid === false &&
				cleanPhone(person.phone).length >= 4
			) {
				addToast({
					message: `${label} phone number is invalid.`,
					variant: 'error',
				});
				return false;
			}
			return true;
		};

		if (purchaseType === 'self') {
			return validatePerson({
				person: buyer,
				fullName: buyerFullName,
				label: 'Buyer',
				phoneValidation: buyerPhoneValidation,
			});
		}

		return (
			validatePerson({
				person: buyer,
				fullName: buyerFullName,
				label: 'Buyer',
				phoneValidation: buyerPhoneValidation,
			}) &&
			validatePerson({
				person: recipient,
				fullName: recipientFullName,
				label: 'Recipient',
				phoneValidation: recipientPhoneValidation,
			})
		);
	};

	const initiatePurchase = async ({
		paymentProvider,
		channels,
	}: {
		paymentProvider: string;
		channels?: string[];
	}) => {
		if (!selectedSalon?.id) return null;
		if (!giftCardCart) return null;

		const buyerName = parseFullName(buyerFullName);
		const recipientName =
			purchaseType === 'gift' ? parseFullName(recipientFullName) : buyerName;

		const recipientToSend: GiftCardRecipient =
			purchaseType === 'gift'
				? { ...recipient, ...recipientName }
				: { ...buyer, ...buyerName };

		const buyerToSend: GiftCardRecipient = { ...buyer, ...buyerName };

		const input: GiftCardPurchaseInput = {
			salonId: selectedSalon.id,
			amount: cartTotalAmount,
			paymentProvider,
			channels,
			message: giftCardCart.message,
			recipient: {
				...recipientToSend,
				firstName: (recipientToSend.firstName || '').trim(),
				lastName: (recipientToSend.lastName || '').trim(),
				email: recipientToSend.email?.trim().toLowerCase(),
				phone: cleanPhone(recipientToSend.phone),
			},
			buyer: {
				...buyerToSend,
				firstName: (buyerToSend.firstName || '').trim(),
				lastName: (buyerToSend.lastName || '').trim(),
				email: buyerToSend.email?.trim().toLowerCase(),
				phone: cleanPhone(buyerToSend.phone),
			},
		};

		const response = await initiateGiftCardPurchase({ variables: { input } });
		const payload = response.data?.initiateGiftCardPurchase;
		if (payload?.status !== 200) {
			addToast({
				message:
					payload?.errors?.[0]?.message ||
					'Unable to initiate gift card purchase.',
				variant: 'error',
			});
			return null;
		}

		return payload.data;
	};

	const handlePaymentClose = () => {
		addToast({ message: 'Payment cancelled', variant: 'error' });
		setLoading(false);
	};

	const completeSuccessfulPayment = () => {
		setSuccessSummary({
			amount: cartTotalAmount,
			message: giftCardCart?.message || '',
			businessName: businessInfo?.name || '',
			currencySymbol,
		});
		clearGiftCardCart();
		setShowSuccessDialog(true);
		setLoading(false);
	};

	const launchPaystackPayment = async (init: any, channels?: string[]) => {
		if (!window.PaystackPop) {
			addToast({
				message: 'Payment system not ready. Please try again.',
				variant: 'error',
			});
			setLoading(false);
			return;
		}

		const paystackKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;
		if (!paystackKey) {
			addToast({
				message: 'Payment system not configured. Please contact support.',
				variant: 'error',
			});
			setLoading(false);
			return;
		}

		const emailToUse = (buyer.email || '').trim();

		try {
			const paymentAuth = init.paymentAuthorization;
			if (!paymentAuth?.accessCode) {
				addToast({
					message: 'Payment authorization failed. Please try again.',
					variant: 'error',
				});
				setLoading(false);
				return;
			}

			const paystackPop = new window.PaystackPop();
			await paystackPop.checkout({
				key: paystackKey,
				email: emailToUse,
				amount: Math.round((init.totalAmountPayable || cartTotalAmount) * currency.factor),
				currency: currency.code,
				accessCode: paymentAuth.accessCode,
				...(channels && { channels }),
				onSuccess: completeSuccessfulPayment,
				onCancel: () => handlePaymentClose(),
				onClose: () => handlePaymentClose(),
			});
		} catch (error) {
			console.error('Paystack checkout error:', error);
			addToast({
				message: 'Failed to initiate payment. Please try again.',
				variant: 'error',
			});
			setLoading(false);
		}
	};

	const launchPocketPayment = async (init: any) => {
		const Pocket = (window as any).Pocket;
		if (!Pocket) {
			addToast({
				message: 'Payment system not ready. Please try again.',
				variant: 'error',
			});
			setLoading(false);
			return;
		}

		const pocketKey = import.meta.env.VITE_POCKET_PUBLIC_KEY;
		if (!pocketKey) {
			addToast({
				message: 'Payment system not configured. Please contact support.',
				variant: 'error',
			});
			setLoading(false);
			return;
		}

		try {
			const pocketBusinessId = import.meta.env.VITE_POCKET_BUSINESS_ID;
			const pocketWalletId = import.meta.env.VITE_POCKET_WALLET_ID;

			const pocketConfig: any = {
				key: pocketKey,
				business_id: pocketBusinessId,
				wallet_id: pocketWalletId,
				amount: Math.round((init.totalAmountPayable || cartTotalAmount) * currency.factor),
				reference: init.paymentReference,
				narration: `Gift card purchase`,
				view: 'popup',
				meta: {
					purpose: 'gift_card_purchase',
					salon_id: selectedSalon?.id,
					reference: init.paymentReference,
					processing_fee_amount: init.processingFeeAmount || 0,
					amount: init.amount,
				},
				onSuccess: completeSuccessfulPayment,
				onClose: () => handlePaymentClose(),
				onPending: () =>
					addToast({
						message:
							'Payment is being processed. Please wait for confirmation.',
						variant: 'warning',
					}),

				onError: (err: any) => {
					addToast({
						message: err?.message || 'Payment failed. Please try again.',
						variant: 'error',
					});
					setLoading(false);
				},
			};

			const payWithPocket = new Pocket(pocketConfig);
			payWithPocket.show();
		} catch (error) {
			console.error('Pocket checkout error:', error);
			addToast({
				message: 'Failed to initiate payment. Please try again.',
				variant: 'error',
			});
			setLoading(false);
		}
	};

	const launchStripePayment = async (init: any) => {
		const paymentAuth = init.paymentAuthorization;
		if (!paymentAuth?.clientSecret) {
			addToast({
				message: 'Payment authorization failed. Please try again.',
				variant: 'error',
			});
			setLoading(false);
			return;
		}

		if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
			addToast({
				message: 'Payment system not configured. Please contact support.',
				variant: 'error',
			});
			setLoading(false);
			return;
		}

		setStripeClientSecret(paymentAuth.clientSecret);
		setShowStripeCheckout(true);
	};

	const handleStripeCheckoutComplete = () => {
		setShowStripeCheckout(false);
		setStripeClientSecret(null);
		completeSuccessfulPayment();
	};

	const handleStripeCheckoutClose = () => {
		setShowStripeCheckout(false);
		setStripeClientSecret(null);
		handlePaymentClose();
	};

	const handleProviderSelect = (option: PaymentOption) => {
		setShowProviderModal(false);

		const config = getPaymentOptionConfig(option, paymentCountryCode);

		setLoading(true);

		const isProviderReady =
			config.provider === 'paystack'
				? paystackLoaded || window.PaystackPop
				: config.provider === 'pocket'
					? pocketLoaded || (window as any).Pocket
					: !!import.meta.env.VITE_STRIPE_PUBLIC_KEY;

		if (!isProviderReady) {
			addToast({
				message:
					config.provider === 'stripe'
						? 'Payment system not configured. Please contact support.'
						: 'Payment provider is still loading. Please try again.',
				variant: 'error',
			});
			setLoading(false);
			return;
		}

		initiatePurchase({
			paymentProvider: config.provider,
			channels: config.channels,
		})
			.then((init) => {
				if (!init) {
					setLoading(false);
					return;
				}

				const feeCandidate =
					typeof init.processingFeeAmount === 'number'
						? init.processingFeeAmount
						: Number(
								(init.totalAmountPayable ?? cartTotalAmount) - cartTotalAmount,
							);
				setProcessingFeeAmount(
					Number.isFinite(feeCandidate) && feeCandidate >= 0 ? feeCandidate : null,
				);

				if (config.provider === 'paystack') {
					launchPaystackPayment(init, config.channels);
					return;
				}

				if (config.provider === 'stripe') {
					launchStripePayment(init);
					return;
				}

				launchPocketPayment(init);
			})
			.catch((error) => {
				console.error('Failed to initiate payment:', error);
				addToast({
					message: 'Failed to initiate payment. Please try again.',
					variant: 'error',
				});
				setLoading(false);
			});
	};

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!validate()) return;
		setShowProviderModal(true);
	};

	const GiftCardSuccessModal = () => {
		if (!showSuccessDialog) return null;

		const businessName =
			successSummary?.businessName || businessInfo?.name || '';
		const amount = successSummary?.amount ?? cartTotalAmount;
		const amountLabel = formatPrice(amount, currency);
		const giftCardMessage =
			successSummary?.message || giftCardCart?.message || '';

		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
				{/* Backdrop */}
				<div className="absolute inset-0 bg-black/50" />

				<div className="relative w-full max-w-xl bg-white rounded-xl shadow-xl">
					<button
						type="button"
						onClick={() => {
							setShowSuccessDialog(false);
							setSuccessSummary(null);
							clearGiftCardCart();
							handleNextStep(BookingStep.INITIAL);
						}}
						className="absolute right-4 top-4 p-2 hover:bg-grey-50 rounded-lg transition-colors"
					>
						<X className="h-5 w-5 text-grey-500" />
					</button>

					{/* Header */}
					<div className="p-6 border-b border-grey-100">
						<h2 className="text-xl font-semibold text-grey-900">
							Purchase Successful!
						</h2>
					</div>

					{/* Content */}
					<div className="p-6 text-center">
						{/* Icon */}
						<div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
							<div className="w-16 h-16 bg-green-300 rounded-full flex items-center justify-center">
								<CheckCircle className="h-8 w-8 text-white" />
							</div>
						</div>

						{/* Title */}
						<h3 className="text-2xl font-semibold text-grey-900 mb-2">
							Congratulations!
						</h3>

						{/* Subtitle */}
						<p className="text-lg text-grey-700 mb-4">
							Your gift card purchase was successful
						</p>

						<div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
							<div className="flex items-start gap-3">
								<CheckCircle className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
								<div className="text-sm text-green-300 text-left">
									<p className="font-medium mb-1">Gift card sent!</p>
									<p>
										A confirmation email will be sent shortly. The gift card
										value of {amountLabel} can be used at {businessName}.
									</p>
								</div>
							</div>
						</div>

						<div className="w-full mb-8">
							<div className="w-full bg-[#0B6B5D] rounded-xl p-8 text-white shadow-sm">
								<div className="flex items-center justify-between">
									<span className="text-xs tracking-widest opacity-80">
										GIFT CARD
									</span>
									<span className="text-xs opacity-80">
										{businessInfo?.name || ''}
									</span>
								</div>

								<div className="mt-8">
									<p className="text-3xl font-semibold">
										{currencySymbol}
										{(Number(amount || 0) || 0).toLocaleString()}
									</p>
								</div>

								<div className="mt-10">
									<p className="text-[10px] tracking-widest opacity-70">
										MESSAGE
									</p>
									<p className="mt-2 text-sm opacity-90 line-clamp-2">
										{giftCardMessage.trim()
											? giftCardMessage
											: 'Enter up to 15 words'}
									</p>
								</div>
							</div>
						</div>

						<Button
							type="button"
							className="w-full bg-green-300 hover:bg-green-400 text-white h-12"
							onClick={() => {
								setShowSuccessDialog(false);
								setSuccessSummary(null);
								handleNextStep(BookingStep.INITIAL);
							}}
						>
							Close
						</Button>
					</div>
				</div>
			</div>
		);
	};

	if (!selectedSalon?.id) {
		return (
			<div className="max-w-2xl mx-auto px-4 py-8 lg:px-20">
				<div className="bg-white rounded-xl border border-grey-100 p-6">
					<h2 className="text-lg font-semibold text-grey-900 mb-2">
						Select a location
					</h2>
					<p className="text-sm text-grey-600 mb-4">
						Please go back and select a location to purchase a gift card.
					</p>
					<Button
						type="button"
						variant="outline"
						onClick={() => handleNextStep(BookingStep.INITIAL)}
					>
						Back
					</Button>
				</div>
			</div>
		);
	}

	if (!(giftCardSetting?.enabled && giftCardSetting?.online !== false)) {
		return (
			<div className="max-w-2xl mx-auto px-4 py-8 lg:px-20">
				<div className="bg-white rounded-xl border border-grey-100 p-6">
					<h2 className="text-lg font-semibold text-grey-900 mb-2">
						Gift cards unavailable
					</h2>
					<p className="text-sm text-grey-600 mb-4">
						This business is not accepting gift card purchases at the moment.
					</p>
					<Button
						type="button"
						variant="outline"
						onClick={() => handleNextStep(BookingStep.INITIAL)}
					>
						Back
					</Button>
				</div>
			</div>
		);
	}

	// if (!giftCardCart) {
	// 	return (
	// 		<div className="max-w-2xl mx-auto px-4 py-8 lg:px-20">
	// 			<div className="bg-white rounded-xl border border-grey-100 p-6">
	// 				<h2 className="text-lg font-semibold text-grey-900 mb-2">
	// 					Gift card checkout
	// 				</h2>
	// 				<p className="text-sm text-grey-600 mb-4">
	// 					Please add a gift card to your cart to continue.
	// 				</p>
	// 				<Button
	// 					type="button"
	// 					variant="outline"
	// 					onClick={() => handleNextStep(BookingStep.INITIAL)}
	// 				>
	// 					Back
	// 				</Button>
	// 			</div>
	// 		</div>
		// 	);
		// }

		const buyerEditedFromMatched =
			!!buyerMatchedClient &&
			buyerMatchedClient.phone === cleanPhone(buyer.phone) &&
			(normalizeSpaces(buyerFullName) !== buyerMatchedClient.fullName ||
				normalizeEmail(buyer.email || '') !== buyerMatchedClient.email);

		const recipientEditedFromMatched =
			purchaseType === 'gift' &&
			!!recipientMatchedClient &&
			recipientMatchedClient.phone === cleanPhone(recipient.phone) &&
			(normalizeSpaces(recipientFullName) !== recipientMatchedClient.fullName ||
				normalizeEmail(recipient.email || '') !== recipientMatchedClient.email);

		return (
			<>
				<GiftCardSuccessModal />
			<PaymentProviderModal
				isOpen={showProviderModal}
				onClose={() => setShowProviderModal(false)}
				onSelectOption={handleProviderSelect}
				loading={loading}
				availableOptions={availablePaymentOptions}
			/>
			<StripeCheckoutModal
				isOpen={showStripeCheckout}
				clientSecret={stripeClientSecret}
				onClose={handleStripeCheckoutClose}
				onComplete={handleStripeCheckoutComplete}
			/>
			<div className="max-w-6xl mx-auto px-4 py-8 lg:px-20">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
					{/* Order summary */}
					<div className="bg-white rounded-xl border border-grey-100 p-6 min-w-0">
						<div className="flex items-start justify-between">
							<div>
								<div className="flex items-center gap-3">
									<button
										type="button"
										onClick={() => handleNextStep(BookingStep.INITIAL)}
										className="text-sm text-grey-600 hover:text-grey-900"
									>
										← {businessInfo?.name}
									</button>
								</div>
								<p className="text-sm text-grey-500 mt-4">
									{selectedSalon?.address}
									{selectedSalon?.city ? `, ${selectedSalon.city}` : ''}
								</p>
							</div>
							{businessInfo?.logoUrl && (
								<img
									src={businessInfo.logoUrl}
									alt={businessInfo.name || 'Business logo'}
									className="w-12 h-12 rounded-lg object-cover border border-grey-100"
								/>
							)}
						</div>

						<div className="mt-6 border-t border-grey-100 pt-6 space-y-4">
							<p className="text-xs font-medium text-grey-500 uppercase tracking-wide">
								Order summary
							</p>

							<div className="flex items-start justify-between">
								<div className="flex-1">
									<p className="text-sm font-medium text-grey-900">
										{giftCardSetting?.name || 'Gift card'}
									</p>
									{giftCardCart?.message?.trim() && (
										<p className="text-xs text-grey-500 line-clamp-2 mt-1">
											{giftCardCart.message}
										</p>
									)}
									{/* <div className="mt-3 inline-flex items-center gap-2">
										<span className="text-xs text-grey-500">Qty:</span>
										<div className="inline-flex items-center border border-grey-200 rounded-lg overflow-hidden">
											<button
												type="button"
												onClick={() =>
													updateGiftCardCartQuantity(Math.max(1, cartQuantity - 1))
												}
												className="px-3 py-1.5 text-grey-700 hover:bg-grey-50 disabled:opacity-50"
												disabled={cartQuantity <= 1}
											>
												−
											</button>
											<span className="px-3 py-1.5 text-sm text-grey-900 border-x border-grey-200">
												{cartQuantity}
											</span>
											<button
												type="button"
												onClick={() =>
													updateGiftCardCartQuantity(cartQuantity + 1)
												}
												className="px-3 py-1.5 text-grey-700 hover:bg-grey-50"
											>
												+
											</button>
										</div>
									</div> */}
								</div>

								<div className="text-right">
									<p className="text-sm font-semibold text-grey-900">
										{formatPrice(cartTotalAmount, currency)}
									</p>
									<button
										type="button"
										onClick={clearGiftCardCart}
										className="mt-2 text-xs text-red-600 hover:text-red-700"
									>
										Remove
									</button>
								</div>
							</div>

							<div className="border-t border-grey-100 pt-4 space-y-2">
								<div className="flex items-center justify-between text-sm">
									<span className="text-grey-600">Total</span>
									<span className="font-semibold text-grey-900">
										{formatPrice(cartTotalAmount, currency)}
									</span>
								</div>
								{processingFeeAmount != null ? (
									<p className="text-xs text-grey-500">
										+ processing fee:{' '}
										{formatPrice(processingFeeAmount, currency)}
									</p>
								) : (
									<p className="text-xs text-grey-500">
										+ processing fee (based on payment method)
									</p>
								)}
							</div>
						</div>
					</div>

					{/* Details form */}
					<div className="bg-white rounded-xl border border-grey-100 p-6 min-w-0 overflow-hidden">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-sm font-medium text-grey-900">
								Enter details
							</h2>
							<span className="text-xs text-grey-500">STEP 3 OF 3</span>
						</div>

						<form onSubmit={handleSubmit} className="space-y-6">
							<div className="space-y-2">
								<p className="text-sm font-medium text-grey-900">
									Who are you purchasing gift card for?
								</p>
								<div className="flex items-center gap-6">
									<label className="inline-flex items-center gap-2 text-sm text-grey-700">
										<input
											type="checkbox"
											checked={purchaseType === 'self'}
											onChange={() => setPurchaseType('self')}
											className="h-4 w-4 accent-green-300"
										/>
										Myself
									</label>
									<label className="inline-flex items-center gap-2 text-sm text-grey-700">
										<input
											type="checkbox"
											checked={purchaseType === 'gift'}
											onChange={() => setPurchaseType('gift')}
											className="h-4 w-4 accent-green-300"
										/>
										Someone else
									</label>
								</div>
							</div>

							<div className="space-y-4">
								<div className="space-y-2">
									<label className="text-sm font-medium text-grey-700">
										Phone number *
									</label>
									<div className="flex flex-col sm:flex-row gap-2 min-w-0">
										<SearchableCountrySelect
											value={buyer.countryCode}
											onChange={(countryCode, callingCode) =>
												setBuyer((p) => ({ ...p, countryCode, callingCode }))
											}
											className="w-full sm:w-[160px] shrink-0 px-3 py-3 justify-between"
										/>
										<input
											type="tel"
											value={formatPhoneDisplay(buyer.phone)}
											onChange={(e) => {
												const value = e.target.value.replace(/[^0-9]/g, '');
												setBuyer((p) => ({ ...p, phone: value }));
											}}
											className={`w-full sm:flex-1 min-w-0 border rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300 ${
												buyerPhoneValidation.isValid === true
													? 'border-green-500'
													: buyerPhoneValidation.isValid === false &&
														  cleanPhone(buyer.phone).length >= 4
														? 'border-red-500'
														: 'border-grey-200'
											}`}
											placeholder="801 234 5678"
											required
										/>
									</div>
									{buyerSearchLoading && (
										<div className="flex items-center gap-2 mt-2">
											<Loader2 className="h-4 w-4 animate-spin text-green-600" />
											<span className="text-sm text-green-600">
												Searching for existing account...
											</span>
										</div>
									)}
									{!buyerPhoneValidation.loading &&
										buyerPhoneValidation.isValid === false &&
										cleanPhone(buyer.phone).length >= 4 && (
											<p className="text-red-500 text-xs mt-1">
												Invalid phone number
											</p>
										)}
								</div>

								<div className="space-y-2">
									<label className="text-sm font-medium text-grey-700">
										Full Name *
									</label>
									<input
										value={buyerFullName}
										onChange={(e) => setBuyerFullName(e.target.value)}
										className="w-full border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300"
										placeholder="Jane Okon"
										required
										type="text"
									/>
									{buyerEditedFromMatched && (
										<p className="text-amber-600 text-xs mt-1">
											You’re editing details from an existing customer profile.
											These changes will update their details.
										</p>
									)}
								</div>

								<div className="space-y-2">
									<label className="text-sm font-medium text-grey-700">
										Email *
									</label>
									<input
										type="email"
										value={buyer.email || ''}
										onChange={(e) =>
											setBuyer((p) => ({ ...p, email: e.target.value }))
										}
										className="w-full border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300"
										placeholder="janeokon@gmail.com"
										required
									/>
									{buyerEditedFromMatched && (
										<p className="text-amber-600 text-xs mt-1">
											You’re editing details from an existing customer profile.
											These changes will update their details.
										</p>
									)}
								</div>
							</div>

							{purchaseType === 'gift' && (
								<div className="space-y-4 pt-4 border-t border-grey-100">
									<h3 className="text-sm font-semibold text-grey-900">
										Recipient
									</h3>

									<div className="space-y-2">
										<label className="text-sm font-medium text-grey-700">
											Phone number *
										</label>
										<div className="flex flex-col sm:flex-row gap-2 min-w-0">
											<SearchableCountrySelect
												value={recipient.countryCode}
												onChange={(countryCode, callingCode) =>
													setRecipient((p) => ({
														...p,
														countryCode,
														callingCode,
													}))
												}
												className="w-full sm:w-[160px] shrink-0 px-3 py-3 justify-between"
											/>
											<input
												type="tel"
												value={formatPhoneDisplay(recipient.phone)}
												onChange={(e) => {
													const value = e.target.value.replace(/[^0-9]/g, '');
													setRecipient((p) => ({ ...p, phone: value }));
												}}
												className={`w-full sm:flex-1 min-w-0 border rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300 ${
													recipientPhoneValidation.isValid === true
														? 'border-green-500'
														: recipientPhoneValidation.isValid === false &&
															  cleanPhone(recipient.phone).length >= 4
															? 'border-red-500'
															: 'border-grey-200'
												}`}
											placeholder="801 234 5678"
											required
										/>
									</div>
									{recipientSearchLoading && (
										<div className="flex items-center gap-2 mt-2">
											<Loader2 className="h-4 w-4 animate-spin text-green-600" />
											<span className="text-sm text-green-600">
												Searching for existing account...
											</span>
										</div>
									)}
									{!recipientPhoneValidation.loading &&
										recipientPhoneValidation.isValid === false &&
										cleanPhone(recipient.phone).length >= 4 && (
											<p className="text-red-500 text-xs mt-1">
													Invalid phone number
												</p>
											)}
									</div>

									<div className="space-y-2">
										<label className="text-sm font-medium text-grey-700">
											Name *
										</label>
											<input
												value={recipientFullName}
												onChange={(e) => setRecipientFullName(e.target.value)}
												className="w-full border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300"
												placeholder="Recipient name"
												required
											/>
											{recipientEditedFromMatched && (
												<p className="text-amber-600 text-xs mt-1">
													You’re editing details from an existing customer
													profile. These changes will update their details.
												</p>
											)}
										</div>

									<div className="space-y-2">
										<label className="text-sm font-medium text-grey-700">
											Email *
										</label>
											<input
												type="email"
												value={recipient.email || ''}
												onChange={(e) =>
													setRecipient((p) => ({ ...p, email: e.target.value }))
												}
												className="w-full border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300"
												placeholder="recipient@email.com"
												required
											/>
											{recipientEditedFromMatched && (
												<p className="text-amber-600 text-xs mt-1">
													You’re editing details from an existing customer
													profile. These changes will update their details.
												</p>
											)}
										</div>
								</div>
							)}

							<Button
								type="submit"
								variant="primary"
								className="w-full"
								disabled={
									loading ||
									(buyerPhoneValidation.isValid === false &&
										cleanPhone(buyer.phone).length >= 4) ||
									(purchaseType === 'gift' &&
										recipientPhoneValidation.isValid === false &&
										cleanPhone(recipient.phone).length >= 4)
								}
							>
								{loading ? (
									<span className="inline-flex items-center gap-2">
										<Loader2 className="h-4 w-4 animate-spin" />
										Processing…
									</span>
								) : (
									'Continue'
								)}
							</Button>
						</form>
					</div>
				</div>
			</div>
		</>
	);
}
